import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Event } from "../model/event";
import { listEvents as listEventsApi } from "../services/event";
import { normalizeText } from "../helpers/utils";

type EventInitializationState = Partial<Record<"gallery", boolean>>;

export interface EventContextModel {
  events: Event[];
  filteredEvents: Event[];
  searchQuery: string;
  hasLoadedEvents: boolean;
  hasInitialized: EventInitializationState;
  setHasInitialized: Dispatch<SetStateAction<EventInitializationState>>;
  isLoading: boolean;
  error: string | null;
  listEvents: () => Promise<Event[]>;
  setSearchQuery: (query: string) => void;
  searchEvents: (query: string) => Event[];
}

const EventContext = createContext<EventContextModel | undefined>(undefined);

export const EventProvider = ({ children }: { children: ReactNode }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [hasLoadedEvents, setHasLoadedEvents] = useState(false);
  const [hasInitialized, setHasInitialized] =
    useState<EventInitializationState>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listEvents = useCallback(async (): Promise<Event[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await listEventsApi();
      const enrichedEvents = result.events.map((event) => ({
        ...event,
        normalizedTitle: normalizeText(event.title),
        normalizedDescription: normalizeText(event.description ?? ""),
      }));
      setEvents(enrichedEvents);
      setHasLoadedEvents(true);
      return enrichedEvents;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const searchEvents = useCallback(
    (query: string): Event[] => {
      const normalizedQuery = normalizeText(query);
      if (!normalizedQuery) return events;

      return events.filter((event) => {
        const titleNorm = event.normalizedTitle ?? normalizeText(event.title);
        const descNorm = event.normalizedDescription ?? normalizeText(event.description ?? "");
        return titleNorm.includes(normalizedQuery) || descNorm.includes(normalizedQuery);
      });
    },
    [events],
  );

  const filteredEvents = useMemo(
    () => searchEvents(searchQuery),
    [searchEvents, searchQuery],
  );

  const value: EventContextModel = {
    events,
    filteredEvents,
    searchQuery,
    hasLoadedEvents,
    hasInitialized,
    setHasInitialized,
    isLoading,
    error,
    listEvents,
    setSearchQuery,
    searchEvents,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export const useEvents = (): EventContextModel => {
  const ctx = useContext(EventContext);
  if (!ctx) {
    throw new Error("useEvents must be used within <EventProvider>");
  }
  return ctx;
};
